/**
 * Draggable character card grid for assigning roles within a SIM level.
 *
 * @module components/sim/createLevels/Characters
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */
import React from 'react';
import PropTypes from 'prop-types';
import {Card, CardContent, Grid} from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import {useDrag} from 'react-dnd';
import {useCharacterStyle} from './Characters.js';
import '../dragDrop/style.css';
import CharacterAvatar from "../../common/CharacterAvatar";

/**
 * Renders a draggable character card, displaying character details, avatar, and background color.
 * The card can be double-clicked to trigger additional actions such as showing character details.
 *
 * @function Characters
 * @param {Object} props - The properties passed to the component.
 * @param {Object} props.character - The character details to be displayed on the card.
 *                                    Contains properties like `name`, `type`, and `_id`.
 * @param {string} props.bgColor - Background color applied to the card.
 * @param {string} props.avatar - URL or reference to the avatar image for the character.
 * @param {Function} props.showDetails - Function to execute when the card is double-clicked,
 *                                       typically used for displaying more character details.
 * @returns {JSX.Element|null} A grid containing the styled character card, or null if
 *                             the character object is not defined.
 */
const Characters = ({
    character,
    bgColor,
    avatar,
    showDetails,
}) => {
    const classes = useCharacterStyle();
    const [{opacity}, drag] = useDrag({
        type: 'sidebarItem',
        item: {id: character._id, character},
        collect: (monitor) => ({
            opacity: monitor.isDragging() ? 0.4 : 1,
        }),
    });

    if (character) {
        return (
            <Grid
                ref={drag}
                style={{opacity}}
                className={classes.characterGrid}
                item
                xs={12}
                sm={12}
                md={12}
            >
                <Grid>
                    <Card
                        className={classes.card}
                        style={{background: bgColor}}
                        onDoubleClick={showDetails}
                    >
                        <CardContent className={classes.cardContent}>
                            <div className={classes.details}>
                                <CharacterAvatar avatar={avatar} alt={character.type}/>
                                <div className={classes.characterDiv}>
                                    <Typography className={classes.characterName}>
                                        {character.name}
                                    </Typography>
                                    <Typography className={classes.characterRole}>
                                        The
                                        {' '}
                                        {character.masked_type ?
                                            character.masked_type :
                                            character.type}
                                    </Typography>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        );
    }

    return null;
};

Characters.propTypes = {
    character: PropTypes.object,
    bgColor: PropTypes.string,
    avatar: PropTypes.string,
    showDetails: PropTypes.func,
};

export default Characters;
